import { fetchWeatherLetter, type S3WeatherProduct } from '../../../hooks/data/weather';
import { type LocalDB } from '../../LocalDB/LocalDB';
import {
    FlightSupplementalDataType,
    type FlightSupplementalMetar,
    type FlightSupplementalTaf,
} from '../FlightSupplementalDataTypes';
import { type UpcomingReservationEvent } from './useUpcomingReservations';

// A TAF lives 36 hours at most: it describes nothing further ahead, and a stored copy older than that is of no use.
const TAF_LIFETIME_MS = 36 * 3600 * 1e3;
// The TAF files on S3 are regenerated by a cron job every 20 minutes; METARs are downloaded on the same schedule.
const TAF_UPDATE_INTERVAL_MS = 20 * 60 * 1e3;
// Between two downloads of a letter at least, also when the cron is late (Last-Modified long past) or a
// download fails.
const MIN_DOWNLOAD_INTERVAL_MS = 5 * 60 * 1e3;

/** Letter's DB id (per product) → time (ms) before which it is not downloaded again. */
const nextDownloadByLetter = new Map<string, number>();
/** Letters' DB ids whose last download failed: retried only when due, even without a stored copy. */
const failedLetters = new Set<string>();

/** All TAFs of airports whose ICAO starts with `letter`, as stored on S3 (one file per letter). */
export type SupplementalDataTaf = {
    letter: string;
    /** When the letter was downloaded; also anchors the day/hour-only times of the TAFs when parsed. */
    fetchedAt: Date;
    /** ICAO → raw TAF text. */
    tafs: Record<string, string>;
};

/** All METARs of airports whose ICAO starts with `letter`, as stored on S3 (one file per letter). */
export type SupplementalDataMetar = {
    letter: string;
    fetchedAt: Date;
    /** ICAO → raw METAR text. */
    metars: Record<string, string>;
};

export type StoredTaf = { taf: string; fetchedAt: Date };
export type StoredMetar = { metar: string; fetchedAt: Date };

/** Events a TAF may be issued for: not over yet, and starting within a TAF's lifetime. */
export function getEventsWithinTafLifetime(
    events: UpcomingReservationEvent[],
    now: number = Date.now()
): UpcomingReservationEvent[] {
    return events.filter(
        (event) => event.event.dateTo.getTime() > now && event.event.dateFrom.getTime() < now + TAF_LIFETIME_MS
    );
}

export const loadTafs = async (db: LocalDB, icaos: string[]) => loadWeather('taf', db, icaos);
export const loadMetars = async (db: LocalDB, icaos: string[]) => loadWeather('metar', db, icaos);

async function loadWeather(product: S3WeatherProduct, db: LocalDB, icaos: string[]): Promise<void> {
    const letters = [...new Set(icaos.map((icao) => icao.toUpperCase()[0]))].filter((letter) => /^[A-Z]$/.test(letter));
    const dueLetters = (
        await Promise.all(
            letters.map(async (letter) =>
                (await isLetterDue(db, getSupplementalWeatherDbId(product, letter))) ? [letter] : []
            )
        )
    ).flat();
    if (!dueLetters.length) {
        return;
    }
    const results = await Promise.allSettled(dueLetters.map(async (letter) => downloadLetter(product, letter)));
    const records = results.flatMap((result, index) => {
        if (result.status === 'rejected') {
            // the previously stored copy of the letter stays in place, a sync 5 minutes later tries again
            const letter = dueLetters[index];
            const id = getSupplementalWeatherDbId(product, letter);
            console.error(`Error fetching ${product.toUpperCase()}s for letter ${letter}`, result.reason);
            nextDownloadByLetter.set(id, Date.now() + MIN_DOWNLOAD_INTERVAL_MS);
            failedLetters.add(id);

            return [];
        }

        return [result.value];
    });
    if (records.length) {
        await db.flightSupplementalData.bulkPut(records);
    }
}

async function isLetterDue(db: LocalDB, id: string): Promise<boolean> {
    const nextDownload = nextDownloadByLetter.get(id);
    if (nextDownload === undefined || nextDownload <= Date.now()) {
        return true;
    }
    if (failedLetters.has(id)) {
        return false;
    }

    // not updated on S3 yet, but the stored copy is gone (e.g. the local database was wiped)
    return !(await db.flightSupplementalData.get(id));
}

async function downloadLetter(
    product: S3WeatherProduct,
    letter: string
): Promise<FlightSupplementalMetar | FlightSupplementalTaf> {
    const { reports, lastModified } = await fetchWeatherLetter(product, letter);
    const id = getSupplementalWeatherDbId(product, letter);
    const fetchedAt = new Date();
    nextDownloadByLetter.set(
        id,
        Math.max(
            (lastModified ?? fetchedAt).getTime() + TAF_UPDATE_INTERVAL_MS,
            fetchedAt.getTime() + MIN_DOWNLOAD_INTERVAL_MS
        )
    );
    failedLetters.delete(id);
    const record = { id, usefulnessEndsByDate: new Date(fetchedAt.getTime() + TAF_LIFETIME_MS) };

    return product === 'taf'
        ? {
              ...record,
              type: FlightSupplementalDataType.taf,
              taf: { letter, fetchedAt, tafs: Object.fromEntries(reports) },
          }
        : {
              ...record,
              type: FlightSupplementalDataType.metar,
              metar: { letter, fetchedAt, metars: Object.fromEntries(reports) },
          };
}

export function getTafFromLetter(letter: SupplementalDataTaf, icao: string): StoredTaf | null {
    const taf = letter.tafs[icao.toUpperCase()];

    return taf ? { taf, fetchedAt: letter.fetchedAt } : null;
}

export function getMetarFromLetter(letter: SupplementalDataMetar, icao: string): StoredMetar | null {
    const metar = letter.metars[icao.toUpperCase()];

    return metar ? { metar, fetchedAt: letter.fetchedAt } : null;
}

export const getSupplementalTafDbId = (letter: string) => getSupplementalWeatherDbId('taf', letter);
export const getSupplementalMetarDbId = (letter: string) => getSupplementalWeatherDbId('metar', letter);

function getSupplementalWeatherDbId(product: S3WeatherProduct, letter: string): string {
    return `${product}-${letter.toLowerCase()}`;
}
